/**
 * `<ChatMessageList>` — the conversation rendered as a retro terminal scrollback (2026-05-23).
 *
 * Two sources, rendered as one terminal session:
 *  - the session's **persisted** `ChatMessage[]` — prior turns, fetched from
 *    `GET /chat/sessions/{id}` (the agent loop persisted them);
 *  - the **in-flight** `StreamingTurn` from `useChatStream` — the current turn, growing live as
 *    SSE events fold in.
 *
 * A user turn is echoed as a typed command (`|< <question>`); the assistant turn
 * is the command's output below it. A persisted assistant message can carry `tool_use` blocks
 * (the agent's prior tool calls); those render as `<ToolCallAffordance>` rows, so a re-opened
 * session shows the same agentic transparency the live turn showed.
 *
 * The terminal `error` state renders inline (editor-chat.md §1.3 — graceful failure is a
 * *designed state*, not an exception path): an `iteration_limit` keeps the partial answer
 * above the notice; a `model_unavailable` shows the degradation message.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { searchHybrid } from '@/api';
import { friendlyToolLabel, ToolCallAffordance } from './ToolCallAffordance';
import { MarkdownChatText } from './MarkdownChatText';
import { TERMINAL_PROMPT } from './ChatComposer';
import { ThinkingBox } from './ThinkingBox';
import { useTypewriterText } from './useTypewriterText';
import type {
  ChatErrorCategory,
  ChatMessage,
  ChatToolCallView,
  StreamingTurn,
} from './chatTypes';

/**
 * `useResearchElapsed` — tracks when the two-stage pipeline transitions from
 * researching to synthesizing, so the collapsed `<ThinkingBox>` summary can render
 * "Researched 3 notes in 4s".
 *
 * Returns the elapsed seconds at the moment `synthesizing` first arrived, or `null`
 * until then. Once captured, the value freezes (we don't want the summary to keep
 * ticking up after the box collapses).
 */
function useResearchElapsed(streamingTurn: StreamingTurn | null): number | null {
  const startedAtRef = useRef<number | null>(null);
  const [capturedSeconds, setCapturedSeconds] = useState<number | null>(null);

  // Start the clock when the first tool call OR the 'drafting' phase event arrives —
  // whichever comes first signals the researcher began.
  const researcherKicked =
    streamingTurn !== null &&
    (streamingTurn.toolCalls.length > 0 || streamingTurn.multipassPhase === 'drafting');
  const isSynthesizing =
    streamingTurn !== null && streamingTurn.multipassPhase === 'synthesizing';

  useEffect(() => {
    if (researcherKicked && startedAtRef.current === null) {
      startedAtRef.current = Date.now();
    }
    if (isSynthesizing && capturedSeconds === null && startedAtRef.current !== null) {
      setCapturedSeconds(Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000)));
    }
  }, [researcherKicked, isSynthesizing, capturedSeconds]);

  // Reset when a fresh turn starts (no streaming turn or new turn id surrogate via
  // multipassPhase resetting to null on phase=='drafting' first emission of new turn).
  useEffect(() => {
    if (streamingTurn === null) {
      startedAtRef.current = null;
      setCapturedSeconds(null);
    }
  }, [streamingTurn]);

  return capturedSeconds;
}

/** `<ChatMessageList>` props — the persisted history + the in-flight turn (if any). */
export interface ChatMessageListProps {
  /** The session's persisted messages — prior turns. */
  messages: ChatMessage[];
  /** The in-flight streaming turn — `null` when no turn is running. */
  streamingTurn: StreamingTurn | null;
  /** Whether a turn is currently streaming — drives the "thinking" indicator. */
  isStreaming: boolean;
  /** The in-flight turn's user query — the thinking indicator previews a real hybrid search on
   *  it (streamed out as filler) while the agent works. */
  pendingUserQuery?: string;
}

/** A human label for a terminal error category — the graceful-failure inline notice's text. */
function errorNotice(category: ChatErrorCategory, message: string): { heading: string; body: string } {
  switch (category) {
    case 'iteration_limit':
      // NOT a failure — a clean bounded terminal state; the partial answer above is kept.
      return { heading: 'Reached the step limit', body: message };
    case 'spend_cap':
      return { heading: 'Spend cap reached', body: message };
    case 'model_unavailable':
      return { heading: 'Chat model unavailable', body: message };
    case 'tool_system_error':
    case 'malformed_output':
      return { heading: 'Something went wrong', body: message };
  }
}

/**
 * `<ChatErrorNotice>` — the terminal-error state, rendered inline (editor-chat.md §1.3).
 *
 * Shows the friendly heading + the backend's actual message + the correlation id, and adds a
 * **Copy report** affordance (the same pattern the system-error toast uses). The copied report
 * is the full diagnostic — category, correlation id, and raw message — so a bug report carries
 * the reference that joins to the backend's structured logs instead of a screenshot of a
 * sanitized sentence.
 */
function ChatErrorNotice({
  error,
}: {
  error: { category: ChatErrorCategory; message: string; correlationId: string };
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const notice = errorNotice(error.category, error.message);
  const hasRef = error.correlationId !== 'n/a';

  // Flip "Copied" back to "Copy report" after a moment.
  useEffect(() => {
    if (!copied) return undefined;
    const t = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(t);
  }, [copied]);

  // The full report a bug filing needs — category + reference (joins the server logs) + the
  // raw backend message. Plain text so it pastes cleanly into an issue or a chat.
  const report = [
    'Aletheia chat error report',
    `category: ${error.category}`,
    hasRef ? `reference: ${error.correlationId}` : null,
    `message: ${error.message}`,
  ]
    .filter((line): line is string => line !== null)
    .join('\n');

  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(report);
      setCopied(true);
    } catch {
      // Clipboard unavailable (sandboxed/HTTP) — the message + reference are still
      // selectable inline as a manual fallback.
    }
  }, [report]);

  return (
    <div className={`chat-error-notice chat-error-notice--${error.category}`} role="status">
      <div className="chat-error-notice__heading">{notice.heading}</div>
      <div className="chat-error-notice__body">{notice.body}</div>
      {hasRef && <div className="chat-error-notice__ref">Reference: {error.correlationId}</div>}
      <div className="chat-error-notice__actions">
        <button
          type="button"
          className="chat-error-notice__copy"
          onClick={() => void onCopy()}
          data-copied={copied || undefined}
          title="Copy a diagnostic report (category, reference, message) for a bug report"
        >
          {copied ? 'Copied' : 'Copy report'}
        </button>
      </div>
    </div>
  );
}

/** The echoed prompt line — `|< <command>` — for a user turn. */
function PromptEcho({ command }: { command: string }): JSX.Element {
  return (
    <div className="chat-term__cmd">
      <span className="chat-term__prompt-user">{TERMINAL_PROMPT}</span>
      <span className="chat-term__cmd-text">{command}</span>
    </div>
  );
}

/** Concatenate a user message's text blocks into the single command string it echoes as. */
function userCommandText(message: ChatMessage): string {
  return message.content
    .filter((b): b is { type: 'text'; text: string } => b.type === 'text')
    .map((b) => b.text)
    .join('\n');
}

/** Render the ordered content of a persisted ASSISTANT message — output text + tool_use rows. */
function PersistedAssistantBody({ message }: { message: ChatMessage }): JSX.Element {
  return (
    <>
      {message.content.map((block, i) => {
        if (block.type === 'text') {
          // A persisted text block — rendered as GFM markdown with inline `[[note:UUID]]`
          // citation pills hydrated to clickable titles.
          return (
            <div key={i} className="chat-term__output">
              <MarkdownChatText text={block.text} />
            </div>
          );
        }
        if (block.type === 'tool_use') {
          // A persisted tool call — render the command line in the same terminal style the live
          // search log uses (the stored block carries no result summary, so just the command).
          const args = Object.keys(block.args).join(', ');
          return (
            <div key={block.callId} className="chat-term__sl-call">
              <div className="chat-term__sl-cmd">
                <span className="chat-term__sl-prompt">$</span> {block.toolName}
                {args !== '' ? ` ${args}` : ''}
              </div>
            </div>
          );
        }
        // A `tool_result` block on a synthetic user message — the agent's plumbing.
        return (
          <div key={`${block.callId}-r`} className="chat-term__plumbing">
            {block.isError ? '[tool result · recovered from error]' : '[tool result · ok]'}
          </div>
        );
      })}
    </>
  );
}

/** " · round N of M" when the loop has announced a round; empty string otherwise. */
function roundSuffix(turn: StreamingTurn): string {
  if (turn.currentRound === null) return '';
  const cap = turn.maxRounds !== null ? ` of ${turn.maxRounds}` : '';
  return ` · round ${turn.currentRound}${cap}`;
}

/** " · condensed N earlier results" when the loop condensed older tool-results; empty otherwise. */
function condensedSuffix(turn: StreamingTurn): string {
  if (turn.compressedResults <= 0) return '';
  const noun = turn.compressedResults === 1 ? 'result' : 'results';
  return ` · condensed ${turn.compressedResults} earlier ${noun}`;
}

/**
 * The CURRENT-activity label for the in-flight turn — what the app is doing *right now*, so
 * the user isn't staring at an opaque "Thinking…" through a 10–50s round. Made HONEST
 * (transparency ask): when a tool is running we keep the friendly tool label and append the
 * round; when no tool is running but a model round is in flight we name it outright —
 * "Consulting the model · round 3 of 8 · condensed 2 earlier results".
 */
function currentActivityLabel(turn: StreamingTurn): string {
  const running = turn.toolCalls.find((tc) => tc.status === 'running');
  if (running) {
    // A tool IS running — keep the existing friendly label, append the round for context.
    return `${friendlyToolLabel(running.toolName, running.argsSummary)}${roundSuffix(turn)}`;
  }
  // No tool running. If a model round is in flight, say so honestly with its progress.
  if (turn.currentRound !== null) {
    return `Consulting the model${roundSuffix(turn)}${condensedSuffix(turn)}`;
  }
  if (turn.multipassPhaseLabel !== null) return turn.multipassPhaseLabel;
  if (turn.toolCalls.length > 0) return 'Synthesizing answer…';
  return 'Thinking…';
}

/**
 * `<ChatLiveStatus>` — the in-progress status line: the current-activity label + a live
 * elapsed-time counter ("· 23s") + a blinking terminal cursor. Mounted only while a turn is
 * streaming and has not yet begun emitting its final answer text, so the user always sees
 * *what* is happening and *how long* it has been running rather than dead air.
 */
function ChatLiveStatus({ turn }: { turn: StreamingTurn }): JSX.Element {
  const mountedAtRef = useRef<number>(Date.now());
  const [elapsedS, setElapsedS] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setElapsedS(Math.floor((Date.now() - mountedAtRef.current) / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="chat-term__status" aria-live="polite" data-testid="chat-live-status">
      <span className="chat-term__status-label">{currentActivityLabel(turn)}</span>
      {elapsedS > 0 && <span className="chat-term__status-elapsed"> · {elapsedS}s</span>}
      <span className="chat-term-cursor" aria-hidden="true" />
    </div>
  );
}

/** Round a coarse token count to a compact "~Xk" / "~N" label — the "size of what we're
 *  sending" the timeline surfaces per model round. */
function approxTokenLabel(tokens: number): string {
  if (tokens >= 1000) return `~${Math.round(tokens / 1000)}k tokens`;
  return `~${tokens} tokens`;
}

/**
 * `<ChatActivityTimeline>` — the honest, collapsible record of what the turn actually did
 * (the transparency ask): the model round it's on (round N — sent ~Xk tokens) plus every
 * tool call it made, so the user can SEE the back-and-forth with the model and the data
 * flowing back, not just a "Thinking…" spinner.
 *
 * Collapsed by default with a small "show activity" toggle — the conversation stays the
 * focus (same philosophy as the collapsible `<ToolCallAffordance>`). It reuses that
 * affordance for the tool rows (friendly label + status + result titles when expanded),
 * matching the existing terminal/mono design language rather than introducing a new style.
 *
 * Mounted for the in-flight turn once there is anything to show (a model round announced or a
 * tool called). The `StreamingTurn` view-model carries only the LATEST round number (not a
 * per-round history), so the timeline shows the current round as one header line above the
 * ordered tool calls — the honest summary of "we are on round N of M, here is what ran".
 */
function ChatActivityTimeline({
  turn,
  toolCalls,
}: {
  turn: StreamingTurn;
  /** The tool calls to render as rows — the parent passes the prebundle-filtered list. */
  toolCalls: ChatToolCallView[];
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const hasRound = turn.currentRound !== null;
  const hasTools = toolCalls.length > 0;
  if (!hasRound && !hasTools) return null;

  const cap = turn.maxRounds !== null ? ` of ${turn.maxRounds}` : '';
  const sent =
    turn.approxInputTokens !== null ? ` — sent ${approxTokenLabel(turn.approxInputTokens)}` : '';

  return (
    <div className="chat-term__activity" data-testid="chat-activity-timeline">
      <button
        type="button"
        className="chat-term__activity-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          font: 'inherit',
          fontFamily: 'var(--font-mono)',
          fontSize: '11px',
          color: 'var(--fg-4, var(--fg-3))',
          letterSpacing: '0.02em',
        }}
      >
        {open ? '▾' : '▸'} {open ? 'hide activity' : 'show activity'}
      </button>
      {open && (
        <div
          className="chat-term__activity-body"
          style={{ marginTop: '4px', paddingLeft: '12px' }}
        >
          {hasRound && (
            <div
              className="chat-term__activity-round"
              style={{
                fontSize: '11px',
                color: 'var(--fg-4, var(--fg-3))',
                fontFamily: 'var(--font-mono)',
                letterSpacing: '0.02em',
              }}
            >
              › Round {turn.currentRound}
              {cap}
              {sent}
              {turn.compressedResults > 0 &&
                ` · condensed ${turn.compressedResults} earlier ${
                  turn.compressedResults === 1 ? 'result' : 'results'
                }`}
            </div>
          )}
          {toolCalls.map((tc) => (
            <ToolCallAffordance key={tc.callId} toolCall={tc} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Typewriter cadence + the pause between titles. The thinking stream is filler — it never
 *  needs to finish, it just fills dead air until the real answer arrives.
 *
 *  2026-05-29 (#100): doubled 8 → 16 cps per Chris's feedback that 4-8 cps was too slow.
 *  2026-05-30 (#102 follow-up): doubled again 16 → 32 cps. The new two-stage pipeline keeps
 *  the typewriter visible through the WHOLE researcher phase (not just pre-first-tool), so
 *  a brisk type-out reads as "in flight" rather than dragging. */
const THINKING_CPS = 32;
const THINKING_PAUSE_MS = 2000;

/**
 * `<ThinkingSearchStream>` — the "thinking" state, as a real preview of retrieval. On mount it
 * runs an actual `search_hybrid` on the user's query (a separate live call from the agent's own
 * loop — search works even when the chat model is slow/unavailable), then reveals the top result
 * note titles ONE AT A TIME: each title types out at ~8 c/s, pauses ~2s, then the next title
 * fires, up to 6. Genuine data, never persisted; the parent unmounts it the instant the real
 * answer text (or the agent's own tool log) takes over.
 */
function ThinkingSearchStream({
  query,
  prebundleTitles,
}: {
  query: string;
  /**
   * transparency: real pre-bundle titles from the streaming turn (the backend's first-turn
   * "prebundle" tool-result). When present + non-empty we stream THESE instead of firing an
   * independent `search_hybrid` — the filler becomes the agent's actual retrieval. `null` on
   * later turns (no prebundle) → we fall back to the independent search below.
   */
  prebundleTitles?: string[] | null;
}): JSX.Element {
  const q = query.trim();
  const hasPrebundle = prebundleTitles != null && prebundleTitles.length > 0;
  const search = useQuery({
    // Skip the independent search entirely when real prebundle titles are available.
    queryKey: ['chat-thinking-search', q],
    enabled: q !== '' && !hasPrebundle,
    staleTime: 60_000,
    queryFn: async () => {
      // Swallow errors into an empty list — this is cosmetic filler; a search hiccup must never
      // surface as an error here (and keeps the query from rejecting / tripping lint).
      try {
        const res = await searchHybrid({ query: q, limit: 6 });
        return res.data.items;
      } catch {
        return [];
      }
    },
  });

  // The first 6 result titles to stream, one per line — the real prebundle titles when the
  // backend supplied them, else the independent search's top hits.
  const titles = useMemo<string[]>(() => {
    if (hasPrebundle) return (prebundleTitles as string[]).slice(0, 6);
    if (q === '' || !search.isSuccess) return [];
    const items = search.data ?? [];
    if (items.length === 0) return ['no direct matches — reasoning from context'];
    return items.slice(0, 6).map((it) => it.title);
  }, [hasPrebundle, prebundleTitles, q, search.isSuccess, search.data]);

  // Staged reveal: type the current title char-by-char, pause, advance to the next.
  const [line, setLine] = useState(0);
  const [chars, setChars] = useState(0);
  const [phase, setPhase] = useState<'typing' | 'pausing' | 'done'>('typing');

  useEffect(() => {
    setLine(0);
    setChars(0);
    setPhase('typing');
  }, [titles]);

  useEffect(() => {
    if (titles.length === 0 || phase === 'done') return undefined;
    const current = titles[line] ?? '';
    if (phase === 'typing') {
      if (chars < current.length) {
        const t = window.setTimeout(() => setChars((c) => c + 1), 1000 / THINKING_CPS);
        return () => window.clearTimeout(t);
      }
      const t = window.setTimeout(() => setPhase('pausing'), 0);
      return () => window.clearTimeout(t);
    }
    // phase === 'pausing' — hold, then fire the next title (or finish).
    const t = window.setTimeout(() => {
      if (line + 1 < titles.length) {
        setLine((l) => l + 1);
        setChars(0);
        setPhase('typing');
      } else {
        setPhase('done');
      }
    }, THINKING_PAUSE_MS);
    return () => window.clearTimeout(t);
  }, [titles, line, chars, phase]);

  // Before results land (or with no query), hold on a simple pulsing line.
  if (titles.length === 0) {
    return (
      <div className="chat-term__status" data-testid="chat-live-status">
        <span className="chat-term__status-label">{q === '' ? 'Thinking' : 'searching corpus'}</span>
        <span className="chat-term-cursor" />
      </div>
    );
  }

  return (
    <div className="chat-term__tw" data-testid="chat-thinking-search" aria-label="thinking">
      {titles.slice(0, line + 1).map((title, i) => (
        <div key={i} className="chat-term__tw-line">
          <span className="chat-term__tw-mark">›</span> {i < line ? title : title.slice(0, chars)}
          {i === line && <span className="chat-term-cursor" />}
        </div>
      ))}
    </div>
  );
}

/**
 * The terminal scrollback. Renders persisted turns, then — if a turn is in flight — the
 * streaming turn's growing output + tool affordances + (on a terminal `error`) the
 * graceful-failure notice.
 */
export function ChatMessageList({
  messages,
  streamingTurn,
  isStreaming,
  pendingUserQuery,
}: ChatMessageListProps): JSX.Element {
  const researchElapsedSeconds = useResearchElapsed(streamingTurn);
  // `synthesizing` is the two-stage pipeline's "writer call opening" signal — the
  // <ThinkingBox> collapses to a single-line summary when it arrives. On the legacy
  // single-stage path this never fires, so the box just stays expanded as a scrolling
  // 3-line viewport — also a UX upgrade over the unbounded LiveSearchLog.
  const isSynthesizing =
    streamingTurn !== null && streamingTurn.multipassPhase === 'synthesizing';

  // transparency: on the FIRST turn of a session the backend emits a pre-bundle tool-result
  // (callId "prebundle") carrying the real retrieval titles. When present, the thinking-stream
  // filler shows THOSE actual titles instead of firing its own independent `search_hybrid`
  // (later turns have no prebundle → the stream falls back to the independent search).
  const prebundleTitles = useMemo<string[] | null>(() => {
    const pb = streamingTurn?.toolCalls.find((tc) => tc.callId === 'prebundle');
    return pb?.resultTitles && pb.resultTitles.length > 0 ? pb.resultTitles : null;
  }, [streamingTurn?.toolCalls]);

  // The tool calls the user actually cares to see as rows — the prebundle is a synthetic
  // first-turn retrieval surfaced as the thinking-stream filler (above), so exclude it from
  // the ThinkingBox log and the activity timeline to avoid presenting it twice.
  const displayToolCalls = useMemo(
    () => streamingTurn?.toolCalls.filter((tc) => tc.callId !== 'prebundle') ?? [],
    [streamingTurn?.toolCalls],
  );

  // Smooth character-by-character playback of the streaming bubble text (the
  // formatter's polished output, or the writer's draft on the legacy path).
  // The raw SSE deltas arrive in chunks of varying size; the typewriter reveals
  // them one char at a time with a blinking cursor at the end — same visual
  // language as the search-preview typewriter in <ThinkingSearchStream>.
  const { visibleText, isCatchingUp } = useTypewriterText(
    streamingTurn?.text ?? '',
  );

  return (
    <div className="chat-term" data-testid="chat-message-list">
      {/* --- the session's persisted turns --- */}
      {messages.map((message) => {
        const isToolResultTurn =
          message.role === 'user' && message.content.every((b) => b.type === 'tool_result');

        if (isToolResultTurn) {
          // A synthetic tool-result user message — the agent's plumbing, rendered dim.
          return (
            <div key={message.id} className="chat-term__turn chat-term__turn--plumbing">
              <PersistedAssistantBody message={message} />
            </div>
          );
        }

        if (message.role === 'user') {
          return (
            <div key={message.id} className="chat-term__turn chat-term__turn--user">
              <PromptEcho command={userCommandText(message)} />
            </div>
          );
        }

        return (
          <div key={message.id} className="chat-term__turn chat-term__turn--asst">
            <PersistedAssistantBody message={message} />
          </div>
        );
      })}

      {/* --- the in-flight streaming turn --- */}
      {streamingTurn !== null && (
        <>
          {/* Echo the just-sent command immediately so it stays on screen through streaming
              (a real terminal keeps your typed line above the output). It is NOT yet in the
              persisted `messages` (those only refetch after the turn completes), so render it
              from `pendingUserQuery` — but suppress it the moment the persisted copy lands, to
              avoid a duplicate during the brief post-done refetch window. */}
          {pendingUserQuery !== undefined &&
            pendingUserQuery.trim() !== '' &&
            !messages.some(
              (m) => m.role === 'user' && userCommandText(m).trim() === pendingUserQuery.trim(),
            ) && (
              <div className="chat-term__turn chat-term__turn--user">
                <PromptEcho command={pendingUserQuery} />
              </div>
            )}
          <div className="chat-term__turn chat-term__turn--asst chat-term__turn--streaming">
            {/* The agent's REAL retrieval log, rendered inline through the bounded
              <ThinkingBox> pane (#102 PR 3). The box stays expanded (3-line scrolling
              viewport) while the researcher works; on a `phase: synthesizing` event it
              collapses to a one-line summary ("Researched 3 notes in 4s"). The writer's
              bubble streams below it. Clicking the collapsed summary re-expands. */}
          {(displayToolCalls.length > 0 || isSynthesizing) && (
            <ThinkingBox
              toolCalls={displayToolCalls}
              isSynthesizing={isSynthesizing}
              researchElapsedSeconds={researchElapsedSeconds}
            />
          )}
          {/* The honest, collapsible activity timeline (transparency ask) — the model round
              in flight + every tool call, so the user can SEE the back-and-forth and the data
              flowing back. Collapsed by default; the conversation stays the focus. Rendered
              whenever the turn has announced a round or run a (non-prebundle) tool. */}
          {(streamingTurn.currentRound !== null || displayToolCalls.length > 0) && (
            <ChatActivityTimeline turn={streamingTurn} toolCalls={displayToolCalls} />
          )}
          {/* The streaming output — `streamingTurn.text` grows as SSE deltas arrive,
              but we render the BUFFERED typewriter playback (`visibleText`) so the
              characters appear one-by-one rather than in jumpy chunks. A blinking
              cursor sits at the end while the typewriter is still catching up. */}
          {visibleText !== '' && (
            <div className="chat-term__output">
              <MarkdownChatText text={visibleText} />
              {isCatchingUp && <span className="chat-term-cursor" aria-hidden="true" />}
            </div>
          )}
          {/* Pre-answer feedback while streaming, gated on `visibleText` (not
              `streamingTurn.text`) so it stays up through the brief gap between the first SSE
              delta arriving and the typewriter starting to play it. Three-way precedence:

              1. When real PREBUNDLE titles are present (first turn), stream THOSE as the filler
                 — the "search preview" typewriter is now the agent's actual retrieval, honest
                 rather than a decorative independent search.
              2. Else once a model round / synthesizing / polishing is in flight, the honest
                 <ChatLiveStatus> takes over ("Consulting the model · round N of M · condensed…").
              3. Else (pre-round, single-stage) the independent search filler masks the latency.
              The live status is suppressed while a tool is actively running (the ThinkingBox /
              activity timeline show that step). */}
          {isStreaming &&
            visibleText === '' &&
            streamingTurn.phase === 'streaming' &&
            (prebundleTitles !== null ||
              (streamingTurn.currentRound === null &&
                streamingTurn.multipassPhase !== 'synthesizing' &&
                streamingTurn.multipassPhase !== 'polishing')) && (
              <ThinkingSearchStream
                query={pendingUserQuery ?? ''}
                prebundleTitles={prebundleTitles}
              />
            )}
          {isStreaming &&
            visibleText === '' &&
            streamingTurn.phase === 'streaming' &&
            prebundleTitles === null &&
            (streamingTurn.currentRound !== null ||
              streamingTurn.multipassPhase === 'synthesizing' ||
              streamingTurn.multipassPhase === 'polishing') &&
            !streamingTurn.toolCalls.some((tc) => tc.status === 'running') && (
              <ChatLiveStatus turn={streamingTurn} />
            )}
          {/* The terminal `error` state — rendered INLINE (editor-chat.md §1.3). The partial
              `text` above is kept; the notice carries a copyable diagnostic report. */}
          {streamingTurn.phase === 'error' && streamingTurn.error !== null && (
            <ChatErrorNotice error={streamingTurn.error} />
          )}
          </div>
        </>
      )}
    </div>
  );
}
